import { useEffect, useState, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { EVENT_NAME, ReconciliationReport } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { useUpdateReconciliationReportPaymentStatus } from 'hooks/useClientDataServices';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { totalSettledAmount } from './helpers';
import { labelMessages, errorMessages } from './messages';
import { ReconciliationToast } from './ReconciliationToast';
import {
  getSelectedReconcileRecords,
  getReconcileRecordsPayload,
  clearSelectedReconcileRecords,
  setRetryRecords,
  getRetryRecords
} from './slice';
import {
  StyledSaveButton,
  StyledReconciledHeader,
  StyledPayablesSelected
} from './styled';
import { PaymentStatusUpdateDataType } from './types';

export const ReconciliationHeader = (): React.JSX.Element => {
  const selectedReconcileRecords = useAppSelector(getSelectedReconcileRecords);
  const reconcileRecordsPayload = useAppSelector(getReconcileRecordsPayload);
  const retryRecords = useAppSelector(getRetryRecords);
  const [message, setMessage] = useState<string>();
  const [errorList, setErrorList] = useState<string[]>([]);
  const retryToastFlag = useRef<boolean>(true);
  const [shouldShowFailureToast, setShouldShowFailureToast] =
    useState<boolean>(false);
  const intl = useIntl();
  const amplitude = useAmplitude();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { mutate, isLoading, data } =
    useUpdateReconciliationReportPaymentStatus();
  const retryFailedRecordsPayload = retryRecords?.filter(record =>
    data?.errorList.includes(record.payableId)
  );

  const mutateData = (reconcilePayload: PaymentStatusUpdateDataType) => {
    mutate(reconcilePayload, {
      onSuccess: async () => {
        dispatch(setRetryRecords(reconcilePayload));
        dispatch(clearSelectedReconcileRecords());
        await queryClient.invalidateQueries([ReconciliationReport]);
      },
      onError: () => {
        dispatch(
          setErrorMessages([intl.formatMessage(errorMessages.allRecordsFailed)])
        );
      }
    });
  };

  useEffect(() => {
    if (data?.errorList.length && retryToastFlag.current) {
      if (data?.payableIds.length === 0) {
        setMessage(intl.formatMessage(errorMessages.allRecordsFailed));
        setErrorList([]);
      } else if (data?.errorList.length > 10) {
        setMessage(
          intl.formatMessage(errorMessages.greaterThanTenFailed, {
            count: data?.errorList?.length
          })
        );
        setErrorList([]);
      } else {
        setMessage(intl.formatMessage(errorMessages.lessThanTenFailed));
        setErrorList(data?.errorList);
      }
      setShouldShowFailureToast(true);
    }
  }, [dispatch, data, intl]);

  const handleSaveButtonClick = () => {
    retryToastFlag.current = true;
    if (!selectedReconcileRecords.length) {
      dispatch(
        setErrorMessages([intl.formatMessage(errorMessages.saveButton)])
      );
    } else {
      mutateData(reconcileRecordsPayload);
      amplitudeTracker(amplitude, EVENT_NAME.ReconcilePayments, {
        'Credit Card': totalSettledAmount(reconcileRecordsPayload)
      });
    }
  };

  const handleRetry = () => {
    mutateData(retryFailedRecordsPayload);
    retryToastFlag.current = false;
    setShouldShowFailureToast(false);
  };

  const handleDismiss = () => setShouldShowFailureToast(false);

  return (
    <>
      {shouldShowFailureToast && (
        <ReconciliationToast
          handleDismiss={handleDismiss}
          handleRetry={handleRetry}
          errorMessage={message}
          errorList={errorList}
        />
      )}
      <StyledReconciledHeader data-testid="reconciled-header">
        {selectedReconcileRecords?.length > 0 && (
          <StyledPayablesSelected>{`${selectedReconcileRecords?.length} ${
            selectedReconcileRecords?.length > 1
              ? intl.formatMessage(labelMessages.payablesSelected)
              : intl.formatMessage(labelMessages.payableSelected)
          }`}</StyledPayablesSelected>
        )}
        <StyledSaveButton
          dataTestId="reconcile-save-button"
          text={
            <FormattedMessage
              description="save button label"
              defaultMessage="Save"
            />
          }
          isLoading={isLoading}
          onClick={handleSaveButtonClick}
        />
      </StyledReconciledHeader>
    </>
  );
};
